import { normalizeSuiteId } from './mlsShared.js';
import { applyMlsServerSnapshot, getSessionManagerMLS } from './sessionManagerMLS.js';
import { deriveGroupSyncState, requiresRuntimeBootstrap } from './conversationSyncState.js';
export { normalizeSuiteId } from './mlsShared.js';

const toStringOrNull = (value) => {
  if (value == null) return null;
  try {
    const normalized = String(value);
    return normalized.trim() ? normalized : null;
  } catch {
    return null;
  }
};

const compactObject = (input = {}) => {
  const out = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined) {
      out[key] = value;
    }
  }
  return out;
};

const readySyncInFlight = new Map();
const managerFlightIds = new WeakMap();
let nextManagerFlightId = 1;

const getManagerFlightId = (manager) => {
  if (!manager || typeof manager !== 'object') return 'manager:none';
  const existing = managerFlightIds.get(manager);
  if (existing) return existing;
  const created = `manager:${nextManagerFlightId}`;
  nextManagerFlightId += 1;
  managerFlightIds.set(manager, created);
  return created;
};

const toReadySyncKey = ({
  managerId = null,
  conversationId = null,
  groupId = null,
  groupHandle = null,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedGroupId = toStringOrNull(groupId);
  const normalizedGroupHandle = toStringOrNull(groupHandle);
  const laneKey =
    normalizedConversationId || normalizedGroupId || normalizedGroupHandle || 'no-group';
  return [
    toStringOrNull(managerId) || 'manager:none',
    normalizedConversationId || 'no-conversation',
    laneKey,
  ].join('|');
};

export const resolveMlsManager = (options = {}) => {
  const {
    manager,
    userId = null,
    identityName = null,
    preferredCiphersuite = undefined,
    wantPQ = undefined,
    serverManaged = true,
  } = options || {};

  if (manager && typeof manager === 'object') {
    return manager;
  }

  try {
    if (typeof window !== 'undefined' && window.sessionManagerMLS) {
      return window.sessionManagerMLS;
    }
  } catch {}

  return getSessionManagerMLS({
    userId,
    identityName,
    preferredCiphersuite,
    wantPQ,
    serverManaged,
  });
};

export const ensureBootstrapKeyPackagePublished = async ({
  manager,
  userId = null,
  identityName = null,
  preferredCiphersuite = undefined,
  identityId = null,
  clientId = null,
  conversationId = null,
  groupId = null,
  groupHandle = null,
  forcePublish = false,
} = {}) => {
  const resolvedManager = resolveMlsManager({
    manager,
    userId,
    identityName,
    preferredCiphersuite,
  });
  if (!resolvedManager) return null;

  const normalizedSuite = normalizeSuiteId(preferredCiphersuite);
  const ensuredIdentity =
    (await resolvedManager.ensureIdentity?.({
      ciphersuite: normalizedSuite,
      name: identityName || undefined,
    })) ?? null;
  const normalizedIdentityId = toStringOrNull(
    identityId ?? ensuredIdentity?.identityId ?? ensuredIdentity?.identity_id ?? null,
  );
  const normalizedClientId = toStringOrNull(
    clientId ??
      ensuredIdentity?.clientId ??
      ensuredIdentity?.client_id ??
      resolvedManager.identityClientId ??
      null,
  );
  const publishDisposition =
    (await resolvedManager.getBootstrapPublishDisposition?.({
      identityId: normalizedIdentityId,
      clientId: normalizedClientId,
      conversationId: toStringOrNull(conversationId),
      groupId: toStringOrNull(groupId),
      groupHandle: toStringOrNull(groupHandle),
      ciphersuite: normalizedSuite,
    })) ?? null;

  if (!publishDisposition?.allowPublish) {
    return {
      skipped: true,
      deferred: Boolean(publishDisposition?.deferred),
      reason: publishDisposition?.reason ?? 'publish_not_required',
      identityId: publishDisposition?.identityId ?? normalizedIdentityId,
      clientId:
        publishDisposition?.clientId ?? normalizedClientId ?? resolvedManager.identityClientId ?? null,
      ciphersuite: publishDisposition?.ciphersuite ?? normalizedSuite ?? null,
      targetRef: publishDisposition?.targetRef ?? null,
      source: publishDisposition?.source ?? null,
    };
  }

  const publishResult =
    (await resolvedManager.publishKeyPackageAndSync?.(
      publishDisposition?.identityId ?? normalizedIdentityId,
      {
        forcePublish,
        retryOnMissingClientId: true,
      },
    )) ?? null;

  return {
    ...publishResult,
    identityId:
      publishResult?.identityId ?? publishDisposition?.identityId ?? normalizedIdentityId,
    clientId:
      publishResult?.clientId ??
      publishResult?.client_id ??
      resolvedManager.identityClientId ??
      normalizedClientId,
    ciphersuite: publishResult?.ciphersuite ?? normalizedSuite ?? null,
  };
};

export const ensureConversationGroup = async ({
  manager,
  conversation = null,
  conversationId = null,
  groupId = null,
  groupHandle = null,
  identityId = null,
  ciphersuite = null,
  userId = null,
  identityName = null,
  wantPQ = undefined,
  skipInternalRecovery = false,
  skipInternalWait = false,
} = {}) => {
  const resolvedManager = resolveMlsManager({
    manager,
    userId,
    identityName,
    preferredCiphersuite: ciphersuite,
    wantPQ,
  });
  if (!resolvedManager) return null;

  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedGroupId = toStringOrNull(groupId);
  const normalizedGroupHandle = toStringOrNull(groupHandle);
  const normalizedIdentityId = toStringOrNull(identityId);
  const normalizedCiphersuite = normalizeSuiteId(ciphersuite);

  if (typeof resolvedManager.ensureGroupForConversation === 'function') {
    return resolvedManager.ensureGroupForConversation(
      compactObject({
        conversation,
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        identityId: normalizedIdentityId,
        ciphersuite: normalizedCiphersuite,
      }),
      {
        __skipRecovery: skipInternalRecovery === true,
        __skipWait: skipInternalWait === true,
        __allowMismatchSelfJoin: false,
      },
    );
  }

  if (typeof resolvedManager.ensureGroup === 'function') {
    return resolvedManager.ensureGroup(
      compactObject({
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        identityId: normalizedIdentityId,
        ciphersuite: normalizedCiphersuite,
      }),
      {
        __skipWait: skipInternalWait === true,
      },
    );
  }

  return null;
};

export const getConversationGroupInfo = async ({
  manager,
  groupId = null,
  groupHandle = null,
} = {}) => {
  const resolvedManager = resolveMlsManager({ manager });
  if (!resolvedManager || typeof resolvedManager.groupInfo !== 'function') {
    return null;
  }
  return resolvedManager.groupInfo(
    compactObject({
      groupId: toStringOrNull(groupId),
      groupHandle: toStringOrNull(groupHandle),
    }),
  );
};

export const convergeConversationGroup = async ({
  manager,
  groupId = null,
  conversationId = null,
  reason = 'manual_converge',
} = {}) => {
  const resolvedManager = resolveMlsManager({ manager });
  if (!resolvedManager || typeof resolvedManager.convergeGroupState !== 'function') {
    return null;
  }
  return resolvedManager.convergeGroupState(
    compactObject({
      groupId: toStringOrNull(groupId),
      conversationId: toStringOrNull(conversationId),
      reason: toStringOrNull(reason) || 'manual_converge',
    }),
  );
};

export const waitUntilGroupReady = async ({ manager, groupId = null, groupHandle = null } = {}) => {
  const resolvedManager = resolveMlsManager({ manager });
  if (!resolvedManager || typeof resolvedManager.waitForGroupReady !== 'function') {
    return null;
  }
  return resolvedManager.waitForGroupReady(
    compactObject({
      groupId: toStringOrNull(groupId),
      groupHandle: toStringOrNull(groupHandle),
    }),
  );
};

export const syncConversationWelcomes = async ({
  manager,
  since = undefined,
  conversationId = null,
  groupId = null,
} = {}) => {
  const resolvedManager = resolveMlsManager({ manager });
  if (!resolvedManager || typeof resolvedManager.syncWelcomes !== 'function') {
    return null;
  }
  return resolvedManager.syncWelcomes(
    compactObject({
      since,
      conversationId: toStringOrNull(conversationId),
      groupId: toStringOrNull(groupId),
    }),
  );
};

export const applyConversationSnapshot = async ({
  manager,
  snapshot,
  source = 'orchestrator',
  traceId = null,
  conversationId = null,
  userId = null,
  identityName = null,
  preferredCiphersuite = undefined,
  wantPQ = undefined,
} = {}) => {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const resolvedManager = resolveMlsManager({
    manager,
    userId,
    identityName,
    preferredCiphersuite,
    wantPQ,
  });

  const payload = compactObject({
    source,
    traceId,
    conversationId: toStringOrNull(conversationId),
    userId: toStringOrNull(userId),
    identityName: toStringOrNull(identityName),
    preferredCiphersuite: normalizeSuiteId(preferredCiphersuite),
    wantPQ,
  });

  if (resolvedManager && typeof resolvedManager.applyServerSnapshot === 'function') {
    return resolvedManager.applyServerSnapshot(snapshot, payload);
  }

  return applyMlsServerSnapshot(snapshot, payload);
};

export const ensureConversationReady = async ({
  manager,
  conversation = null,
  conversationId = null,
  groupId = null,
  groupHandle = null,
  identityId = null,
  ciphersuite = null,
  userId = null,
  identityName = null,
  wantPQ = undefined,
  reason = 'orchestrator_ready_sync',
  runConverge = true,
  forceJoinIfNotReady = true,
  allowRecovery = true,
  waitForReady = true,
} = {}) => {
  const normalizedConversationId = toStringOrNull(
    conversationId ?? conversation?.id ?? conversation?.conversation_id ?? null,
  );
  const normalizedGroupId = toStringOrNull(groupId ?? conversation?.mls_group_id ?? null);
  const normalizedGroupHandle = toStringOrNull(groupHandle);
  const resolvedManager = resolveMlsManager({
    manager,
    userId,
    identityName,
    preferredCiphersuite: ciphersuite,
    wantPQ,
  });
  if (!resolvedManager) return null;
  const requestedRunConverge = runConverge !== false;
  const requestedForceJoinIfNotReady = forceJoinIfNotReady !== false;
  const requestedAllowRecovery = allowRecovery !== false;
  const requestedWaitForReady = waitForReady !== false;
  const managerId = getManagerFlightId(resolvedManager);
  const flightKey = toReadySyncKey({
    managerId,
    conversationId: normalizedConversationId,
    groupId: normalizedGroupId,
    groupHandle: normalizedGroupHandle,
  });

  const activeFlight = readySyncInFlight.get(flightKey);
  if (activeFlight) {
    return activeFlight;
  }

  const task = (async () => {
    const deriveDecisionState = (group) =>
      deriveGroupSyncState(group, {
        ready: group?.ready === true,
        identityIds: [
          toStringOrNull(identityId),
          toStringOrNull(resolvedManager?.identityClientId ?? null),
          toStringOrNull(resolvedManager?.identityId ?? null),
        ].filter(Boolean),
      });
    const classifyRuntimeState = (group) => {
      if (!group) return 'missing';
      return deriveDecisionState(group).runtimeState;
    };
    const classifyCanonicalState = (group) => {
      if (!group) return 'missing';
      return deriveDecisionState(group).canonicalState;
    };
    const shouldAttemptRecovery = (group) => {
      if (!requestedAllowRecovery || typeof resolvedManager.recoverGroup !== 'function') {
        return false;
      }
      if (!group) {
        return false;
      }
      if (classifyRuntimeState(group) === 'runtime_behind' && !group?.needsRecovery) {
        return false;
      }
      return classifyCanonicalState(group) === 'needs_recovery';
    };
    const getPendingWelcomeCount = (group) => {
      return deriveDecisionState(group).pendingWelcomesCount;
    };
    const shouldAllowLastResortForceJoin = (group) => {
      if (!requestedForceJoinIfNotReady || typeof resolvedManager.forceJoinBundle !== 'function') {
        return false;
      }
      if (!group || group.ready) {
        return false;
      }
      if (getPendingWelcomeCount(group) > 0) {
        return false;
      }
      if (!requiresRuntimeBootstrap(deriveDecisionState(group))) {
        return false;
      }
      const runtimeState = classifyRuntimeState(group);
      const canonicalState = classifyCanonicalState(group);
      if (canonicalState !== 'joining' && canonicalState !== 'needs_welcome') {
        return false;
      }
      const derived = deriveDecisionState(group);
      const suppressServerManagedMemberForceJoin =
        resolvedManager?.serverManagedWelcomes !== false &&
        derived.currentIdentityMember &&
        !derived.pendingSetup &&
        !derived.welcomeRequired;
      if (suppressServerManagedMemberForceJoin) {
        return false;
      }
      const runtimeEpoch = derived.runtimeEpoch;
      const snapshotEpoch = derived.targetSnapshotEpoch;
      if (!(snapshotEpoch != null && snapshotEpoch > 0)) {
        return false;
      }
      if (runtimeEpoch != null && runtimeEpoch > 0) {
        return false;
      }
      const recoveryReason = toStringOrNull(
        group?.recoveryReason ?? group?.recovery_reason ?? null,
      );
      return recoveryReason == null || recoveryReason === 'missing_runtime_group';
    };
    const refreshDecisionGroup = async (fallbackGroup = null) => {
      const decisionGroupId =
        fallbackGroup?.groupId ?? normalizedGroupId ?? convergedGroup?.groupId ?? null;
      const decisionGroupHandle =
        fallbackGroup?.groupHandle ?? normalizedGroupHandle ?? convergedGroup?.groupHandle ?? null;
      const currentInfo =
        (await getConversationGroupInfo({
          manager: resolvedManager,
          groupId: decisionGroupId,
          groupHandle: decisionGroupHandle,
        }).catch(() => null)) || fallbackGroup;
      return currentInfo;
    };
    const rerunEnsureGroup = async (fallbackGroup = null) => {
      const rerunGroupId =
        fallbackGroup?.groupId ?? normalizedGroupId ?? convergedGroup?.groupId ?? null;
      const rerunGroupHandle =
        fallbackGroup?.groupHandle ?? normalizedGroupHandle ?? convergedGroup?.groupHandle ?? null;
      ensuredGroup =
        (await ensureConversationGroup({
          manager: resolvedManager,
          conversation,
          conversationId: normalizedConversationId,
          groupId: rerunGroupId,
          groupHandle: rerunGroupHandle,
          identityId,
          ciphersuite,
          userId,
          identityName,
          wantPQ,
          skipInternalRecovery: true,
          skipInternalWait: true,
        })) || fallbackGroup;
      ensuredGroup = await refreshDecisionGroup(ensuredGroup);
      return ensuredGroup;
    };
    const runRecovery = async (group, recoveryReason = null) => {
      const recoveryTarget = group || ensuredGroup || convergedGroup || null;
      if (!recoveryTarget || typeof resolvedManager.recoverGroup !== 'function') {
        return recoveryTarget;
      }
      await resolvedManager.recoverGroup({
        groupId: recoveryTarget?.groupId || normalizedGroupId || null,
        groupHandle: recoveryTarget?.groupHandle || normalizedGroupHandle || null,
        reason:
          recoveryReason ||
          recoveryTarget?.recoveryReason ||
          reason ||
          'needs_recovery',
      });
      return rerunEnsureGroup(recoveryTarget);
    };

    let convergedGroup = null;
    if (requestedRunConverge && typeof resolvedManager.convergeGroupState === 'function') {
      try {
        convergedGroup = await convergeConversationGroup({
          manager: resolvedManager,
          groupId: normalizedGroupId,
          conversationId: normalizedConversationId,
          reason,
        });
      } catch {}
    }

    let ensuredGroup = await ensureConversationGroup({
      manager: resolvedManager,
      conversation,
      conversationId: normalizedConversationId,
      groupId: normalizedGroupId || convergedGroup?.groupId || null,
      groupHandle: normalizedGroupHandle || convergedGroup?.groupHandle || null,
      identityId,
      ciphersuite,
      userId,
      identityName,
      wantPQ,
      skipInternalRecovery: true,
      skipInternalWait: true,
    });
    ensuredGroup = await refreshDecisionGroup(ensuredGroup);

    if (shouldAttemptRecovery(ensuredGroup)) {
      try {
        ensuredGroup = await runRecovery(ensuredGroup);
      } catch {}
    }

    if (shouldAllowLastResortForceJoin(ensuredGroup)) {
      try {
        await resolvedManager.forceJoinBundle({
          conversationId: normalizedConversationId,
          groupId: ensuredGroup?.groupId || normalizedGroupId || convergedGroup?.groupId || null,
          identityId: toStringOrNull(identityId),
          ciphersuite: normalizeSuiteId(ciphersuite),
          // Last-resort only: allow welcome reissue only after reconcile+recovery
          // confirmed that runtime is still missing with no actionable pending welcome.
          preferReissueOnAlreadyMemberNoWelcome: true,
        });
        ensuredGroup =
          (await ensureConversationGroup({
            manager: resolvedManager,
            conversation,
            conversationId: normalizedConversationId,
            groupId: ensuredGroup?.groupId || normalizedGroupId || convergedGroup?.groupId || null,
            groupHandle:
              ensuredGroup?.groupHandle ||
              normalizedGroupHandle ||
              convergedGroup?.groupHandle ||
              null,
            identityId,
            ciphersuite,
            userId,
            identityName,
            wantPQ,
            skipInternalRecovery: true,
            skipInternalWait: true,
          })) || ensuredGroup;
      } catch {}
    }

    let readyInfo = null;
    if (requestedWaitForReady) {
      const waitGroupId = ensuredGroup?.groupId || normalizedGroupId || convergedGroup?.groupId || null;
      const waitGroupHandle =
        ensuredGroup?.groupHandle || normalizedGroupHandle || convergedGroup?.groupHandle || null;
      try {
        readyInfo = await waitUntilGroupReady({
          manager: resolvedManager,
          groupId: waitGroupId,
          groupHandle: waitGroupHandle,
        });
      } catch (error) {
        if (requestedAllowRecovery && error?.code === 'MLS_GROUP_NEEDS_RECOVERY') {
          ensuredGroup = await runRecovery(
            ensuredGroup || convergedGroup || null,
            error?.recoveryReason || null,
          );
          readyInfo = await waitUntilGroupReady({
            manager: resolvedManager,
            groupId:
              ensuredGroup?.groupId || waitGroupId || normalizedGroupId || convergedGroup?.groupId || null,
            groupHandle:
              ensuredGroup?.groupHandle ||
              waitGroupHandle ||
              normalizedGroupHandle ||
              convergedGroup?.groupHandle ||
              null,
          });
        } else {
          throw error;
        }
      }
    }

    const group = readyInfo || ensuredGroup || convergedGroup || null;
    return {
      manager: resolvedManager,
      convergedGroup: convergedGroup || null,
      ensuredGroup: ensuredGroup || null,
      readyInfo: readyInfo || null,
      group,
      needsRecovery: Boolean(group?.needsRecovery),
      recoveryReason: group?.recoveryReason || null,
    };
  })();

  readySyncInFlight.set(flightKey, task);
  try {
    return await task;
  } finally {
    if (readySyncInFlight.get(flightKey) === task) {
      readySyncInFlight.delete(flightKey);
    }
  }
};

export const resetEnsureConversationReadyFlights = () => {
  readySyncInFlight.clear();
};

export default {
  normalizeSuiteId,
  resolveMlsManager,
  ensureConversationGroup,
  ensureConversationReady,
  resetEnsureConversationReadyFlights,
  getConversationGroupInfo,
  convergeConversationGroup,
  waitUntilGroupReady,
  syncConversationWelcomes,
  applyConversationSnapshot,
};
